/**
 * QueueHeaderRow — the segment table's header row. A column that declares a
 * `sortValue` renders as a sort button; every other column renders exactly the
 * plain header it always did, which is what keeps sorting off the segments that
 * never asked for it (this table also serves Live when a layer pill is active).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Info } from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { type QueueColumn } from '../queue-columns';
import QueueSortableTh from './QueueSortableTh';
import { type QueueSortState } from './queue-sort';

interface QueueHeaderRowProps {
  columns: QueueColumn[];
  sort: QueueSortState;
  onSort: (column: QueueColumn) => void;
}

const PlainTh: FC<{ column: QueueColumn }> = ({ column }) => (
  <th
    className={cn(
      'whitespace-nowrap px-2 py-1.5 font-medium',
      column.align === 'right' && 'text-right',
    )}
  >
    {column.hint ? (
      <span
        className={cn(
          'inline-flex items-center gap-1',
          column.align === 'right' && 'justify-end',
        )}
      >
        {column.header}
        <Tooltip content={column.hint} contentClassName="max-w-xs normal-case">
          <Info
            className="h-3 w-3 shrink-0 cursor-help opacity-60"
            aria-label={`About the ${column.header} column`}
          />
        </Tooltip>
      </span>
    ) : (
      column.header
    )}
  </th>
);

const QueueHeaderRow: FC<QueueHeaderRowProps> = ({ columns, sort, onSort }) => (
  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
    <tr>
      {columns.map((c) =>
        c.sortValue ? (
          <QueueSortableTh key={c.key} column={c} sort={sort} onSort={onSort} />
        ) : (
          <PlainTh key={c.key} column={c} />
        ),
      )}
    </tr>
  </thead>
);

export default QueueHeaderRow;
